import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Typography,
    TextField,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Checkbox,
    CircularProgress,
    Box,
    InputAdornment,
    Stack,
} from '@mui/material';
import {
    Close as CloseIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { fetchUsersList } from '../../services/users.service';
import { addClientAdviser, removeClientAdviser } from '../../services/clients.service';
import { useAlert } from '../../functions/useAlert';
import { LinkAdvisersModalProps, User } from './types';

const LinkAdvisersModal: React.FC<LinkAdvisersModalProps> = ({
    open,
    onClose,
    onSave,
    clientId,
    orgId,
    existingAdvisers,
}) => {
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [originalAdviserIds, setOriginalAdviserIds] = useState<Set<string>>(
        new Set()
    );
    const [selectedAdviserIds, setSelectedAdviserIds] = useState<Set<string>>(
        new Set()
    );
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [setAlert] = useAlert();

    // Initialize state when modal opens
    useEffect(() => {
        if (open) {
            // Initialize both sets with existing advisers
            const existingIds = new Set(existingAdvisers.map((a) => a.userId));
            setOriginalAdviserIds(existingIds);
            setSelectedAdviserIds(new Set(existingIds));
            setSearchQuery('');

            // Fetch all users
            fetchAllUsers();
        }
    }, [open, existingAdvisers]);

    const fetchAllUsers = async () => {
        setIsLoading(true);
        try {
            const response: any = await fetchUsersList(orgId);
            setAllUsers(response.users || []);
        } catch (error: any) {
            setAlert(error.message || 'Failed to load users', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // Filter users based on search query
    const filteredUsers = useMemo(() => {
        if (!searchQuery.trim()) return allUsers;

        const query = searchQuery.toLowerCase();
        return allUsers.filter(
            (user) =>
                user.firstName.toLowerCase().includes(query) ||
                user.lastName.toLowerCase().includes(query) ||
                user.email.toLowerCase().includes(query)
        );
    }, [allUsers, searchQuery]);

    const handleToggleUser = (userId: string) => {
        setSelectedAdviserIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) {
                newSet.delete(userId);
            } else {
                newSet.add(userId);
            }
            return newSet;
        });
    };

    const handleSelectAll = () => {
        setSelectedAdviserIds(new Set(filteredUsers.map((u) => u._id)));
    };

    const handleDeselectAll = () => {
        setSelectedAdviserIds(new Set());
    };

    const handleSave = async () => {
        setIsSaving(true);

        try {
            // Calculate additions: items in selected but not in original
            const toAdd = Array.from(selectedAdviserIds).filter(
                (id) => !originalAdviserIds.has(id)
            );

            // Calculate removals: items in original but not in selected
            const toRemove = Array.from(originalAdviserIds).filter(
                (id) => !selectedAdviserIds.has(id)
            );

            // Execute all API calls in parallel
            const addPromises = toAdd.map((userId) =>
                addClientAdviser(clientId, userId)
            );

            const removePromises = toRemove.map((userId) =>
                removeClientAdviser(clientId, userId)
            );

            // Wait for all operations to complete (use allSettled to handle partial failures)
            const results = await Promise.allSettled([
                ...addPromises,
                ...removePromises,
            ]);

            // Check for failures
            const failures = results.filter((r) => r.status === 'rejected');

            if (failures.length === 0) {
                // All operations succeeded
                setAlert('Advisers updated successfully', 'success');
                onSave();
                onClose();
            } else {
                // Some operations failed
                setAlert(
                    `${failures.length} operation(s) failed. Please try again.`,
                    'error'
                );
                // Keep modal open for retry
            }
        } catch (error: any) {
            // Show error notification but keep modal open
            setAlert(error.message || 'Failed to update advisers', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const hasChanges = useMemo(() => {
        if (originalAdviserIds.size !== selectedAdviserIds.size) return true;
        for (const id of originalAdviserIds) {
            if (!selectedAdviserIds.has(id)) return true;
        }
        return false;
    }, [originalAdviserIds, selectedAdviserIds]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: '12px' } }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                }}
            >
                <Typography variant="h6" fontWeight={600}>
                    Manage Advisers
                </Typography>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <Stack spacing={2}>
                    {/* Search Input */}
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by name or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Select All / Deselect All Buttons */}
                    <Box display="flex" gap={1}>
                        <Button size="small" onClick={handleSelectAll} variant="outlined">
                            Select All
                        </Button>
                        <Button
                            size="small"
                            onClick={handleDeselectAll}
                            variant="outlined"
                        >
                            Deselect All
                        </Button>
                    </Box>

                    {/* User List */}
                    {isLoading ? (
                        <Box display="flex" justifyContent="center" py={3}>
                            <CircularProgress />
                        </Box>
                    ) : filteredUsers.length > 0 ? (
                        <List
                            dense
                            sx={{
                                maxHeight: '400px',
                                overflowY: 'auto',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: '8px',
                            }}
                        >
                            {filteredUsers.map((user) => (
                                <ListItem key={user._id} disablePadding>
                                    <ListItemButton
                                        onClick={() => handleToggleUser(user._id)}
                                        dense
                                    >
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <Checkbox
                                                edge="start"
                                                checked={selectedAdviserIds.has(user._id)}
                                                tabIndex={-1}
                                                disableRipple
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={`${user.firstName} ${user.lastName}`}
                                            secondary={`${user.role} - ${user.email}`}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Typography variant="body2" color="text.secondary" align="center">
                            No users found.
                        </Typography>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 1 }}>
                <Button onClick={onClose} variant="outlined" color="info">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={isSaving || !hasChanges}
                >
                    {isSaving ? <CircularProgress size={24} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LinkAdvisersModal;
